import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import {
  Space, Button, Typography, Spin, Empty, message, Tooltip,
  Row, Col, Card, Descriptions, Modal, Table, Image as AntImage, Input,
  Tag,
} from "antd";
import { ArrowLeftOutlined, CopyOutlined } from "@ant-design/icons";
import { getRequest, postRequest } from "../../util/service";
import { generateImagesFromSmiles } from "../../util/ChemDrawUtils";
import dayjs from "dayjs";
import { FilterCriteriaDisplay } from "../shared/FilterCriteriaDisplay";
import DownloadIconButton from "../shared/DownloadIconButton";
import BreadcrumbTrail from "../shared/BreadcrumbTrail";

const { Title, Text } = Typography;

// Helper to format the pH range from the API data
const formatPhRange = (ligprep) => {
    if (!ligprep) return "N/A";
    const base = ligprep.ph;
    const thr = ligprep.ph_threshold;
    if (base !== undefined && thr !== undefined) return `${base.toFixed(0)} ± ${thr.toFixed(1)}`;
    if (base !== undefined) return String(base.toFixed(0));
    return "N/A";
};

const getStatusProps = (status) => {
  const value = String(status || "");
  const lower = value.toLowerCase();
  if (lower.includes("complete")) return { color: "green", text: value || "Completed" };
  if (lower.includes("fail") || lower.includes("error")) return { color: "red", text: value || "Failed" };
  if (lower.includes("progress")) return { color: "blue", text: value || "In Progress" };
  if (lower.includes("terminate") || lower.includes("cancel")) return { color: "orange", text: value || "Terminated" };
  return { color: "default", text: value || "N/A" };
};

export default function GlideMolecularDataPage() {
  const navigate = useNavigate();
  const { projectId, batchId, recordId } = useParams();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [jobDetails, setJobDetails] = useState(null);
  const [molecularData, setMolecularData] = useState([]);

  const [imgCache, setImgCache] = useState({});
  const inFlightRef = useRef(new Set());
  const [tablePage, setTablePage] = useState(1);
  const pageSize = 10;
  const [isPoseModalVisible, setIsPoseModalVisible] = useState(false);
  const [otherPosesData, setOtherPosesData] = useState([]);
  const [searchValue, setSearchValue] = useState(location.state?.prefillMoleculeId || "");

  const fetchMolecularData = useCallback(async () => {
    if (!recordId) return;
    setLoading(true);
    setError("");
    try {
      // ✅ NEW: Fetch all data from the new, self-contained API endpoint
      const response = await getRequest(`/riva/sd/glide/${recordId}`);
      const data = response?.data?.[0];

      if (!data) throw new Error("No data found for this Glide job.");
      
      // Set the job-level details
      setJobDetails(data);

      // Sort and set the molecule-level data
      const sorted = (data.glide_molecule_data || []).slice().sort((a, b) => {
        const aNo = !a.pose_data || a.pose_data.length === 0;
        const bNo = !b.pose_data || b.pose_data.length === 0;
        if (aNo !== bNo) return aNo ? 1 : -1;
        if (aNo && bNo) return 0;
        const aScore = a.pose_data?.[1]?.r_i_docking_score;
        const bScore = b.pose_data?.[1]?.r_i_docking_score;
        if (typeof aScore === "number" && typeof bScore === "number") return aScore - bScore;
        return 0;
      });
      setMolecularData(sorted);

    } catch (err) {
      const errorMessage = err.message || "An error occurred.";
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [recordId]);

  useEffect(() => { fetchMolecularData(); }, [fetchMolecularData]);
  useEffect(() => {
    setTablePage(1);
  }, [searchValue]);

  // Image Generation Logic (unchanged)
  useEffect(() => {
    const start = (tablePage - 1) * pageSize;
    const candidates = molecularData.slice(start, start + 2 * pageSize);
    const toGen = candidates.filter(m => m?.smile_string && !imgCache[m.smile_id] && !inFlightRef.current.has(m.smile_id));
    if (!toGen.length) return;
    toGen.forEach(m => inFlightRef.current.add(m.smile_id));
    (async () => {
      try {
        const urls = await generateImagesFromSmiles(toGen.map(m => m.smile_string));
        const map = {};
        toGen.forEach((m, i) => (map[m.smile_id] = urls[i]));
        setImgCache(prev => ({ ...prev, ...map }));
      } catch {} 
      finally {
        toGen.forEach(m => inFlightRef.current.delete(m.smile_id));
      }
    })();
  }, [molecularData, tablePage, pageSize, imgCache]);

  const handleCloneJob = () => {
    if (!jobDetails) return;
    navigate(`/project/${projectId}/batch/${batchId}/submit`, {
      state: {
        selectedMolecules: jobDetails.molecule_data.map(m => ({ molecule_id: m.smileId, smile: m.smileString })),
        filterCriteria: jobDetails.filter_criteria,
        jobToClone: {
          selection_name: jobDetails.ms_name,
          selection_desc: jobDetails.ms_description,
          method: "glide",
          ph_range: jobDetails.ligprep_input?.ph,
          threshold: jobDetails.ligprep_input?.ph_threshold,
          gridfile: jobDetails.glide_input?.grid_file,
          selection_id: jobDetails.ms_record_id,
        },
      },
    });
  };

  const handleFileDownload = async (fileName) => {
    if (!fileName) return message.error("No file name specified.");
    message.loading({ content: "Preparing download...", key: "file-download" });
    try {
      const payload = { action: "download", file_name: fileName, file_type: "Gridfiles", batch_id: batchId, process_id: recordId };
      const response = await postRequest(`/riva/moleculegenbatches/${projectId}/presigned-url`, payload);
      if (!response?.data?.url) throw new Error(response?.data?.message || "Could not get download link.");
      window.location.href = response.data.url; // Direct download
      message.success({ content: "Download will begin shortly.", key: "file-download", duration: 2 });
    } catch (err) {
      message.error({ content: err.message || "Download failed.", key: "file-download", duration: 2 });
    }
  };

  const showOtherPoses = (record) => {
    setOtherPosesData(record.pose_data?.slice(4) || []);
    setIsPoseModalVisible(true);
  };

  const render3dLink = (poseRecord) => {
    const proteinFile = molecularData.find(md => md.pose_data?.length > 0)?.pose_data?.[0]?.pdb_protein;
    return (
      <Link to={`/project/${projectId}/batch/${batchId}/glide3d/${poseRecord.s_m_title}`} state={{ proteinPdbFile: proteinFile, ligandPdbFile: poseRecord.pdb_filename, glideId: recordId }}>
        View 3D Structure
      </Link>
    );
  };

  const columns = [
    { title: "Molecule ID", dataIndex: "smile_id", key: "moleculeId", width: "15%" },
    { title: "2D Structure", key: "structure_2d", align: "center", width: "25%", render: (_, record) => {
        const img = imgCache[record.smile_id];
        return (
          <div style={{ width: 200, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
            <div style={{ height: 180, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {img ? (
                <AntImage src={img} preview={false} style={{ height: 180, width: 180, objectFit: "contain" }} />
              ) : (
                <Spin />
              )}
            </div>
            <div style={{ width: "80%", borderTop: "1px solid #e0e0e0" }} />
            <Tooltip title="Copy SMILES">
              <Button
                size="small"
                icon={<CopyOutlined />}
                onClick={() => { navigator.clipboard.writeText(record.smile_string || ""); message.success("SMILES Copied!"); }}
              />
            </Tooltip>
          </div>
        );
    }},
    { title: "Docking Score", key: "docking_scores", render: (_, record) => {
        const poses = record.pose_data?.slice(1, 4) || [];
        if (!record.pose_data || record.pose_data.length === 0) return <Text type="secondary">No pose data available.</Text>;
        const nestedColumns = [
          { title: "Docking Score", dataIndex: "r_i_docking_score", key: "dockingScore", render: (s) => s.toFixed(4) },
          { title: "3D Structure", key: "action", render: (_, poseRecord) => render3dLink(poseRecord) },
        ];
        return (
          <>
            <Table bordered columns={nestedColumns} dataSource={poses} rowKey="s_m_title" pagination={false} size="small" />
            {(record.pose_data.length || 0) > 4 && (
              <div style={{ textAlign: "right", marginTop: 8 }}><Button type="primary" ghost size="small" onClick={() => showOtherPoses(record)}>View Other Poses ({record.pose_data.length - 4})</Button></div>
            )}
          </>
        );
    }},
  ];

  const otherPosesColumns = [
    { title: "Docking Score", dataIndex: "r_i_docking_score", key: "dockingScore", render: (s) => s.toFixed(4) },
    { title: "3D Structure", key: "action", render: (_, poseRecord) => render3dLink(poseRecord) },
  ];

  const filteredData = useMemo(() => {
    const term = searchValue.trim().toLowerCase();
    if (!term) return molecularData;
    return molecularData.filter((item) =>
      String(item.smile_id || "").toLowerCase().includes(term)
    );
  }, [searchValue, molecularData]);

 if (loading) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column", // Stack the spinner and text vertically
        alignItems: "center",
        justifyContent: "center",
        height: "calc(100vh - 200px)" // Adjust height to be below the header
      }}>
        <Spin size="large" />
        <Text style={{ marginTop: "16px" }}>Loading Glide Data...</Text>
      </div>
    );
  }
  
  if (error) {
    return <Empty description={error} style={{ marginTop: "48px" }} />;
  }

  return (
    <div style={{ padding: 24, background: "#fff", minHeight: "100vh" }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16, marginTop: 70 }}>
        <Col>
          <Space align="center">
            <Tooltip title="Back to Results"><Button onClick={() => navigate(-1)} icon={<ArrowLeftOutlined />} /></Tooltip>
            <Title level={3} style={{ margin: 0 }}>Glide Result</Title>
          </Space>
        </Col>
      </Row>
      <BreadcrumbTrail />

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Card bordered title={<Text strong>Selection Details</Text>} bodyStyle={{ padding: "12px 16px" }}>
            <Descriptions column={1} layout="horizontal" bordered size="small">
              <Descriptions.Item label="Name">{jobDetails?.ms_name}</Descriptions.Item>
              <Descriptions.Item label="Description">{jobDetails?.ms_description}</Descriptions.Item>
              <Descriptions.Item label="Filter Criteria" contentStyle={{ display: "block", overflow: "hidden" }}><FilterCriteriaDisplay rules={jobDetails?.filter_criteria} /></Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        <Col span={12}>
          <Card bordered title={<Text strong>User Inputs</Text>} bodyStyle={{ padding: "12px 16px" }}>
            <Descriptions column={1} layout="horizontal" bordered size="small">
              <Descriptions.Item label="Grid File">
                <Space>
                  <span>{jobDetails?.glide_input?.grid_file}</span>
                  <DownloadIconButton
                    title="Download Grid File"
                    disabled={!jobDetails?.glide_input?.grid_file}
                    onClick={() => handleFileDownload(jobDetails?.glide_input?.grid_file)}
                  />
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="pH Threshold">{formatPhRange(jobDetails?.ligprep_input)}</Descriptions.Item>
              <Descriptions.Item label="Submitted By">{jobDetails?.submitted_id}</Descriptions.Item>
              <Descriptions.Item label="Submitted Time">{dayjs(jobDetails?.submitted_ts).format("YYYY-MM-DD HH:mm:ss")}</Descriptions.Item>
              {(jobDetails?.status || jobDetails?.status_message) && (
                <Descriptions.Item label="Status / Message">
                  <Space>
                    <Tag color={getStatusProps(jobDetails?.status).color}>{getStatusProps(jobDetails?.status).text}</Tag>
                    {jobDetails?.status_message && <Text type="secondary">{jobDetails.status_message}</Text>}
                  </Space>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        </Col>
      </Row>

      <Space style={{ width: "100%", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", rowGap: 12 }}>
        <Input.Search
          placeholder="Search by Molecule ID"
          allowClear
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onSearch={(val) => setSearchValue(val)}
          style={{ width: 280 }}
        />
        <Button icon={<CopyOutlined />} onClick={handleCloneJob}>Clone Job</Button>
      </Space>

      <Table
        dataSource={filteredData}
        columns={columns}
        rowKey="md_record_id"
        pagination={{ current: tablePage, pageSize, onChange: setTablePage, size: "small" }}
        size="small"
        bordered
        locale={{ emptyText: <Empty description="No molecular data found for this job." /> }}
      />

      <Modal title="Other Poses Data" open={isPoseModalVisible} onCancel={() => setIsPoseModalVisible(false)} footer={[<Button key="back" onClick={() => setIsPoseModalVisible(false)}>Close</Button>]} width={600} destroyOnClose>
        <Table bordered dataSource={otherPosesData} columns={otherPosesColumns} rowKey="s_m_title" pagination={false} scroll={{ y: 300 }} size="small" />
      </Modal>
    </div>
  );
}
